import { MatSidenav, MatSidenavContainer } from '@angular/material/sidenav';
import { Component, inject, OnDestroy, OnInit, Signal, ChangeDetectionStrategy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Router, RouterOutlet } from '@angular/router';
import { catchError, concatMap, exhaustMap, filter, Observable, Subject, take, takeUntil, tap, timer } from 'rxjs';
import { Store } from '@ngxs/store';
import { SidebarService, SidebarTab } from './shared/services/sidebar.service';
import { UserState } from './shared/states/user.state';
import { IUser } from './shared/interfaces/IUser';
import { LoadingMode } from './shared/enums/LoadingMode.enum';
import { HeaderComponent } from './shared/components/header/header.component';
import { HeaderPlaceholderComponent } from './shared/components/header-placeholder/header-placeholder.component';
import { LoadingComponent } from './shared/components/loading/loading.component';
import { FooterComponent } from './shared/components/footer/footer.component';
import { AuthService } from './auth/services/auth.service';
import { ConfirmationDialogService } from './shared/services/confirmation-dialog.service';
import { LoadRecentReleaseNote } from './shared/states/releaseNotes/releaseNotes.actions';
import { IReleaseNotes } from './shared/interfaces/IReleaseNotes';
import { ReleaseNotesState } from './shared/states/releaseNotes/releaseNotes.state';
import { AppOperations } from './app.operations';
import { NotificationService } from './shared/services/notification.service';
import { MobileNavComponent } from './shared/components/mobile-nav/mobile-nav.component';
import { LoadAllActiveNews } from './shared/states/news/news.actions';
import { INewsCreateUpdateItem } from './shared/interfaces/news/INews';
import { NewsState } from './shared/states/news/news.state';
import { LoadPendingNotifications, LoadPersistentNotifications } from './shared/states/notifications/notifications.actions';
import { NotificationsState } from './shared/states/notifications/notifications.state';
import { INotification } from './shared/interfaces/notifications/INotification';
import { NotificationClass } from './shared/interfaces/notifications/NotificationClass';
import { UploadProgressOverlayComponent } from './shared/components/upload-progress-overlay/upload-progress-overlay.component';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    HeaderComponent,
    HeaderPlaceholderComponent,
    LoadingComponent,
    RouterOutlet,
    FooterComponent,
    MatSidenav,
    MatSidenavContainer,
    MobileNavComponent,
    UploadProgressOverlayComponent,
  ],
})
export class AppComponent implements OnInit, OnDestroy {
  protected router = inject(Router);
  protected sidebarService = inject(SidebarService);
  private translate = inject(TranslateService);
  private notificationService = inject(NotificationService);
  private store = inject(Store);
  private confirmationDialogService = inject(ConfirmationDialogService);
  protected authService = inject(AuthService);
  private user$: Observable<IUser> = this.store.select<IUser>(UserState.user);
  protected userIsLoading: Signal<boolean> = this.store.selectSignal<boolean>(UserState.isLoading);
  protected isAuthLoading = this.authService.isAuthLoading();
  protected isGetEmployeeDetailsLoading: Signal<boolean> = this.store.selectSignal<boolean>(UserState.isLoading);
  protected readonly LoadingMode = LoadingMode;
  private destroy$: Subject<boolean> = new Subject<boolean>();
  private currentRelease$: Observable<IReleaseNotes> = this.store.select<IReleaseNotes>(ReleaseNotesState.latestReleaseNote);
  private currentNews$: Observable<INewsCreateUpdateItem[]> = this.store.select<INewsCreateUpdateItem[]>(NewsState.activeNewsList);
  private pendingNotifications$: Observable<INotification[]> = this.store.select<INotification[]>(NotificationsState.pendingNotifications);
  private notificationPollingStarted = false;

  ngOnInit(): void {
    this.routeToRedirect();
    this.initializeLanguageConfig();
    this.initializeAuth();
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  protected isRouterPathUnauthorizedOrMaintenance(): boolean {
    return this.router.url === '/auth/unauthorized' || this.router.url === '/maintenance';
  }

  private initializeAuth(): void {
    if (this.authService.isAuthenticated()) {
      this.user$
        .pipe(
          tap(user => this.checkLoggedInUser(user)),
          takeUntil(this.destroy$),
        )
        .subscribe();
    }
  }

  private initializeLanguageConfig(): void {
    // Clean up any corrupted language data in localStorage
    const storedLang = localStorage.getItem('language');
    if (storedLang) {
      const cleanLang = storedLang.replace(/^["']|["']$/g, '');
      if (cleanLang !== storedLang) {
        localStorage.setItem('language', cleanLang);
      }
      this.translate.setFallbackLang(cleanLang);
      this.translate.use(cleanLang);
    } else {
      this.translate.setFallbackLang('en');
      this.translate.use('en');
    }
  }

  private checkLoggedInUser(user: IUser): void {
    if (user?.gid) {
      this.isAuthLoading = false;
      this.showNewReleaseNotification();
      this.loadPendingNotificationsAndOpenSidebar();
      this.startPersistentNotificationPolling();
    }
  }

  private routeToRedirect(): void {
    const url = window.localStorage.getItem('redirectUrl');
    if (url) {
      void this.router.navigateByUrl(url);
      window.localStorage.removeItem('redirectUrl');
    }
  }

  private showNewReleaseNotification(): void {
    const dispatchAction$ = this.store.dispatch(new LoadRecentReleaseNote());
    dispatchAction$
      .pipe(
        concatMap(() => this.currentRelease$),
        tap(currentRelease => {
          if (AppOperations.isLastVisitTimeBeforeRelease(currentRelease.releaseDate)) {
            this.confirmationDialogService
              .openConfirmationDialog(AppOperations.getReleaseNotesDialogData())
              .afterClosed()
              .pipe(
                filter(confirm => !!confirm),
                tap(() => AppOperations.navigateToReleaseNotes(this.router)),
                takeUntil(this.destroy$),
              )
              .subscribe();
          }
          AppOperations.setLastVisitTime();
        }),
        catchError(() => AppOperations.showErrorMessage(this.notificationService, 'new-release.error.latestRelease')),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  /**
   * Loads pending notifications. Only computed notifications open the sidebar;
   * normal notifications remain available without interrupting app startup.
   */
  private loadPendingNotificationsAndOpenSidebar(): void {
    const dispatchAction$ = this.store.dispatch(new LoadPendingNotifications());
    dispatchAction$
      .pipe(
        concatMap(() => this.pendingNotifications$),
        take(1),
        tap(notifications => {
          const hasComputedNotifications = notifications.some(notification => notification.class === NotificationClass.COMPUTED);
          if (hasComputedNotifications) {
            this.sidebarService.open(SidebarTab.Notifications);
          }

          this.openNewsSideBarForNewActiveNews(!hasComputedNotifications);
        }),
        catchError(() => {
          // Fall back to the news-only behaviour if notifications fail to load.
          this.openNewsSideBarForNewActiveNews(true);
          return AppOperations.showErrorMessage(this.notificationService, 'notifications.error.loadingPendingNotifications');
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  private startPersistentNotificationPolling(): void {
    if (this.notificationPollingStarted) {
      return;
    }

    this.notificationPollingStarted = true;
    timer(60_000, 60_000)
      .pipe(
        exhaustMap(() =>
          this.store
            .dispatch(new LoadPersistentNotifications())
            .pipe(
              catchError(() => AppOperations.showErrorMessage(this.notificationService, 'notifications.error.loadingPendingNotifications')),
            ),
        ),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  private openNewsSideBarForNewActiveNews(allowOpen: boolean): void {
    const dispatchAction$ = this.store.dispatch(new LoadAllActiveNews());
    dispatchAction$
      .pipe(
        concatMap(() => this.currentNews$),
        tap(currentNews => {
          if (allowOpen && AppOperations.isLastVisitTimeBeforeLatestCurrentNews(currentNews)) {
            this.sidebarService.open(SidebarTab.News);
          }
          AppOperations.setLastVisitNewsTime();
        }),
        catchError(() => AppOperations.showErrorMessage(this.notificationService, 'news.notifications.loadingActiveNewsError')),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }
}
